/* eslint-disable import/no-cycle */
import { State } from '@/types/store-types';
import { ActionContext } from 'vuex';
import { Field } from '@/types/custom-object';
import {
  AvailableTypeEnum, ForeignKeyRow, OverrideItem, SystemSchema,
} from '@/composables/custom-data/customData.interfaces';
import { ref } from 'vue';
import { extendCustomSchemaData, saveCustomSchemaData, saveOverrides } from '@/composables/custom-data/customData';
import { buildSystemSchemasForRelationEditor } from '@/composables/custom-data/systemRelationSchemas';

// extend element with the element type for currentEditingElement
enum ElementTypeEnum {
    FIELD = 'field',
    RELATION = 'relation',
    OVERRIDE = 'override',
  }
type FieldWithElementType = Field & { elementType?: ElementTypeEnum.FIELD; isNew?: boolean };
type ForeignKeyRowWithElementType = ForeignKeyRow & { elementType?: ElementTypeEnum.RELATION; isNew?: boolean };
type OverrideItemWithElementType = OverrideItem & { elementType?: ElementTypeEnum.OVERRIDE; isNew?: boolean };
interface CustomObjectState {
    initialized: boolean;
    id_custom_data_definition: number;
    settings: {
        name: string;
        description: string;
    };
    currentEditingElement: FieldWithElementType |
     ForeignKeyRowWithElementType |
     OverrideItemWithElementType |
     null; // used to store the field currently being edited for restoration when canceling edit
    fields: Field[];
    relationships: ForeignKeyRow[];
    overrides: any;
    neededData?: {
      systemSchemas: any[];
      customSchemas: any[];
      existingFields: any[];
      existingRelationships: any[];
      existingOverrides: any;
    };
    refreshViewComponent: number; // used to force the view to refresh when the state changes
}

// Utility function to get next field/relation ID
const getNewElementId = (elements: Field[] | ForeignKeyRow[]): number => {
  if (elements.length === 0) return 1;
  const maxId = Math.max(...elements.map((element: Field | ForeignKeyRow) => element.id));
  return maxId + 1;
};
// Helper to generate a label from a field name (simple PascalCase to spaced label)
const toLabel = (str: string) => str.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

/**
 * Vérifie si un groupe d'overrides a changé
 * @param existingGroup - Groupe d'overrides existants
 * @param currentGroup - Groupe d'overrides actuels
 * @returns true si des changements ont été détectés
 */
function hasOverrideGroupChanged(existingGroup: any[], currentGroup: any[]): boolean {
  // Si les tailles sont différentes, il y a eu des ajouts/suppressions
  if (existingGroup.length !== currentGroup.length) {
    return true;
  }

  // Créer des maps pour comparer les overrides
  const existingMap = new Map<string, any>();
  const currentMap = new Map<string, any>();

  // Indexer par custom_field_name pour la comparaison
  existingGroup.forEach((override: any) => {
    existingMap.set(override.custom_field_name, override);
  });

  currentGroup.forEach((override: any) => {
    currentMap.set(override.custom_field_name, override);
  });

  // Vérifier si tous les overrides existants sont présents et identiques
  const existingEntries = Array.from(existingMap.entries());
  const hasRemovedOrChanged = existingEntries.some(([customFieldName, existingOverride]) => {
    const currentOverride = currentMap.get(customFieldName);

    if (!currentOverride) {
      return true; // Override supprimé
    }

    // Comparer les propriétés importantes
    return (
      existingOverride.priority !== currentOverride.priority
      || existingOverride.custom_field_name !== currentOverride.custom_field_name
      || existingOverride.system_field_name !== currentOverride.system_field_name
      || existingOverride.target_system_schema !== currentOverride.target_system_schema
    );
  });

  if (hasRemovedOrChanged) {
    return true;
  }

  // Vérifier s'il y a de nouveaux overrides
  const hasNewOverride = Array.from(currentMap.keys()).some((customFieldName) => !existingMap.has(customFieldName));
  if (hasNewOverride) {
    return true; // Nouvel override ajouté
  }

  return false; // Aucun changement détecté
}

/**
 * Détecte les changements intelligents dans les overrides
 * @param existingOverrides - Les overrides existants avant modification
 * @param currentOverrides - Les overrides actuels après modification
 * @returns Un objet avec les combinaisons target_system_schema/system_field_name modifiées
 */
export function detectOverrideChanges(existingOverrides: any[], currentOverrides: any[]): Record<string, any[]> {
  const changes: Record<string, any[]> = {};

  // Créer des maps pour un accès rapide
  const existingMap = new Map<string, any>();
  const currentMap = new Map<string, any>();

  // Indexer les overrides existants par combinaison target_system_schema/system_field_name
  existingOverrides.forEach((override: any) => {
    const key = `${override.target_system_schema}::${override.system_field_name}`;
    if (!existingMap.has(key)) {
      existingMap.set(key, []);
    }
    const group = existingMap.get(key);
    if (group) {
      group.push(override);
    }
  });

  // Indexer les overrides actuels par combinaison target_system_schema/system_field_name
  currentOverrides.forEach((override: any) => {
    const key = `${override.target_system_schema}::${override.system_field_name}`;
    if (!currentMap.has(key)) {
      currentMap.set(key, []);
    }
    const group = currentMap.get(key);
    if (group) {
      group.push(override);
    }
  });

  // Détecter les changements
  const allKeys = new Set([...existingMap.keys(), ...currentMap.keys()]);

  allKeys.forEach((key) => {
    const existingGroup = existingMap.get(key) || [];
    const currentGroup = currentMap.get(key) || [];

    // Vérifier s'il y a eu des changements
    const hasChanges = hasOverrideGroupChanged(existingGroup, currentGroup);

    if (hasChanges) {
      // Trier par priorité
      const sortedCurrentGroup = [...currentGroup].sort((a: any, b: any) => parseInt(a.priority.toString(), 10) - parseInt(b.priority.toString(), 10));

      changes[key] = sortedCurrentGroup;
    }
  });

  return changes;
}

export default {
  namespaced: true,
  state: (): CustomObjectState => ({
    initialized: false,
    id_custom_data_definition: 0,
    settings: {
      name: '',
      description: '',
    },
    currentEditingElement: null,
    fields: [],
    relationships: [],
    overrides: [],
    neededData: {
      systemSchemas: [],
      customSchemas: [],
      existingFields: [],
      existingRelationships: [],
      existingOverrides: {},
    },
    refreshViewComponent: 0,
  }),

  getters: {
    isInitialized: (state: CustomObjectState) => state.initialized,
    getUniqueKeys: (state: CustomObjectState) => state.fields.filter((field: Field) => field.unique_key).map((field: Field) => field.name),
    getFields: (state: CustomObjectState) => state.fields,
    getRelationships: (state: CustomObjectState) => state.relationships,
    getRelationById: (state: CustomObjectState) => (id: number) => state.relationships.find((rel: ForeignKeyRow) => rel.id === id),
    getOverrides: (state: CustomObjectState) => state.overrides,
    getIdCustomDataDefinition: (state: CustomObjectState) => state.id_custom_data_definition,
    getSettings: (state: CustomObjectState) => state.settings,
    getCurrentObjectName: (state: CustomObjectState) => state.settings.name,
    isEditMode: (state: CustomObjectState) => state.id_custom_data_definition > 0,
    getCurrentEditingElement: (state: CustomObjectState) => state.currentEditingElement,
    getRefreshViewComponent: (state: CustomObjectState) => state.refreshViewComponent,

    /**
     * Get the needed data for the custom object
     */
    getNeededData: (state: CustomObjectState) => state.neededData,
    getNeededDataExistingFields: (state: CustomObjectState) => state.neededData?.existingFields,
    getNeededDataExistingRelationships: (state: CustomObjectState) => state.neededData?.existingRelationships,
    getNeededDataExistingOverrides: (state: CustomObjectState) => state.neededData?.existingOverrides,
    getCustomSchemaById: (state: CustomObjectState) => (id: number) => state.neededData?.customSchemas.find((schema: any) => parseInt(schema.id_custom_data_definition, 10) === parseInt(id.toString(), 10)),
    getCustomSchemaBySchemaName: (state: CustomObjectState) => (schemaName: string) => state.neededData?.customSchemas.find((schema: any) => schema.name === schemaName),
  },

  mutations: {
    setInitialized: (state: CustomObjectState, payload: boolean) => {
      state.initialized = payload;
    },
    setNeededData: (state: CustomObjectState, payload: {
      systemSchemas: any[];
      customSchemas: any[];
      existingFields: any[];
      existingRelationships: any[];
      existingOverrides: any;
    }) => {
      state.neededData = payload;
    },
    setFields: (state: CustomObjectState, payload: any[]) => {
      state.fields = payload;
    },
    setRelationships: (state: CustomObjectState, payload: any[]) => {
      state.relationships = payload;
    },
    setOverrides: (state: CustomObjectState, payload: any[]) => {
      state.overrides = payload;
    },
    setIdCustomDataDefinition: (state: CustomObjectState, payload: number) => {
      state.id_custom_data_definition = payload;
    },
    updateSettings: (state: CustomObjectState, payload: Partial<{ name: string; description: string }>) => {
      state.settings = { ...state.settings, ...payload };
    },
    setCurrentEditingElement: (state: CustomObjectState, payload: FieldWithElementType | ForeignKeyRowWithElementType | null) => {
      state.currentEditingElement = payload;
    },
    incrementRefreshViewComponent: (state: CustomObjectState) => {
      state.refreshViewComponent += 1;
    },
    resetState: (state: CustomObjectState) => {
      state.initialized = false;
      state.id_custom_data_definition = 0;
      state.settings = {
        name: '',
        description: '',
      };
      state.currentEditingElement = null;
      state.fields = [];
      state.relationships = [];
      state.overrides = [];
      state.neededData = {
        systemSchemas: [],
        customSchemas: [],
        existingFields: [],
        existingRelationships: [],
        existingOverrides: {},
      };
      state.refreshViewComponent = 0;
    },
  },

  actions: {
    initializeCustomObject: ({ state, commit }: ActionContext<CustomObjectState, State>,
      { customDataDefinitionEntity, customObjects, allOverrides }: { customDataDefinitionEntity: any; customObjects: any; allOverrides: any }) => {
      commit('setInitialized', false);
      let newOverrides: any[] = [];
      // if there are overrides, store them in the state
      if (allOverrides && allOverrides.length > 0) {
        // sort by target_system_schema, system_field_name, priority
        const sortedOverrides = allOverrides.sort((a: any, b: any) => {
          // first sort by target_system_schema
          if (a.target_system_schema !== b.target_system_schema) {
            return a.target_system_schema.localeCompare(b.target_system_schema);
          }
          // then by system_field_name
          if (a.system_field_name !== b.system_field_name) {
            return a.system_field_name.localeCompare(b.system_field_name);
          }
          // finally by priority (convert to number for proper sorting)
          return parseInt(a.priority, 10) - parseInt(b.priority, 10);
        });

        // add id to each entry
        newOverrides = sortedOverrides.map((override: any, index: number) => ({
          ...override,
          priority: parseInt(override.priority, 10),
          id: index + 1,
        }));

        // update global state reactively
        commit('setOverrides', newOverrides);
      }

      const systemSchemas = ref<SystemSchema[]>(buildSystemSchemasForRelationEditor());

      const customSchemas = ref<SystemSchema[]>(customObjects.map((customObject: any) => {
        const uniqueKeys: string[] = customObject.unique_keys || [];
        // Build target fields: always 'id' + single unique key (if non-composite)
        const targetFields: any[] = [
          { name: 'id', label: 'ID', type: 'number' },
        ];
        if (uniqueKeys.length === 1) {
          const ukName = uniqueKeys[0];
          const ukType = customObject.fields?.find((f: any) => f.name === ukName)?.type || 'text';
          targetFields.push({ name: ukName, label: toLabel(ukName), type: ukType });
        }
        return {
          name: customObject.table_name,
          id_custom_data_definition: customObject.id_custom_object,
          label: toLabel(customObject.schema_name),
          fields: targetFields,
        };
      }));

      let existingFields = [];
      let existingRelationships = [];
      if (customDataDefinitionEntity) {
        commit('setIdCustomDataDefinition', customDataDefinitionEntity.id_definition);
        commit('updateSettings', {
          name: customDataDefinitionEntity.name || '',
          description: customDataDefinitionEntity.description || '',
        });
        const formattedFields = customDataDefinitionEntity.fields.map((field: any, index: number) => ({
          id: index + 1,
          name: field.name,
          label: field.label || toLabel(field.name),
          type: field.type,
          description: field.description || '',
          required: field.required,
          default: field.default !== undefined ? field.default : null,
          unique_key: (customDataDefinitionEntity.unique_keys || []).includes(field.name),
          options: field.options || [],
        }));
        existingFields = formattedFields.map((field: any) => ({
          id: field.id,
          name: field.name,
        }));
        commit('setFields', formattedFields);

        const formattedRelationships = customDataDefinitionEntity.relationships.map((relationship: any, index: number) => ({
          id: index + 1,
          localField: relationship.sourceField,
          schema_type: relationship.targetSchemaType,
          schema: relationship.targetSchema,
          field: relationship.targetField,
          selectedTargetSchema: [{ type: relationship.targetSchemaType, name: relationship.targetSchema }],
        }));
        existingRelationships = formattedRelationships.map((relationship: any) => ({
          id: relationship.id,
          sourceField: relationship.localField,
        }));

        commit('setRelationships', formattedRelationships);
      }

      commit('setNeededData', {
        systemSchemas,
        customSchemas,
        existingFields,
        existingRelationships,
        existingOverrides: newOverrides,
      });
      commit('setInitialized', true);
    },

    saveCustomObject: async ({ state, commit, getters }: ActionContext<CustomObjectState, State>) => {
      try {
        let result: any = { success: true, error: '' };
        const { name: customObjectName, description: customObjectDescription } = getters.getSettings;
        // get primary keys names
        const uniqueKeys = getters.getUniqueKeys;

        // get formatted fields
        const formattedFields = getters.getFields.map((field: Field) => ({
          name: field.name,
          type: field.type,
          label: field.label ?? '',
          description: field.description,
          required: field.required,
          default: field.default,
          options: field.options || [],
        }));

        // get formatted relationships
        const formattedRelationships = getters.getRelationships.map((relationship: ForeignKeyRow) => {
          let targetSchema = relationship.schema;
          // If targetSchemaType is 'custom', use id_custom_data_definition instead of schema name
          if (relationship.schema_type === 'custom') {
            const customSchema = getters.getCustomSchemaBySchemaName(relationship.schema);
            if (customSchema) {
              targetSchema = customSchema.id_custom_data_definition.toString();
            }
          }
          return {
            sourceField: relationship.localField,
            targetSchemaType: relationship.schema_type,
            targetSchema,
            targetField: relationship.field,
          };
        });

        // if in edit mode, update the custom data definition
        if (getters.isEditMode) {
          // update the custom data definition
          const existingFields = getters.getNeededDataExistingFields;
          const existingRelationships = getters.getNeededDataExistingRelationships;
          const existingFieldNames = existingFields.map((ef: any) => ef.name);
          const newFields = formattedFields.filter((f: any) => !existingFieldNames.includes(f.name));
          const existingRelationshipSourceFields = existingRelationships.map((er: any) => er.sourceField);
          const newRelationships = formattedRelationships.filter((rel: any) => !existingRelationshipSourceFields.includes(rel.sourceField));

          if (newFields.length > 0 || newRelationships.length > 0) {
            const dataToSend = {
              name: customObjectName,
              description: customObjectDescription,
              fields: newFields,
              relationships: newRelationships,
            };
            const saveResult = await extendCustomSchemaData(dataToSend, getters.getIdCustomDataDefinition);
            result = { success: saveResult.success, error: saveResult.success ? '' : (saveResult.error || 'Une erreur est survenue lors de la sauvegarde du schéma') };
          }
        } else {
          // create the custom data definition
          const dataDefinition = {
            name: customObjectName,
            description: customObjectDescription,
            unique_keys: uniqueKeys.length > 0 ? uniqueKeys : undefined,
            fields: formattedFields,
            relationships: formattedRelationships,
          };
          const saveResult = await saveCustomSchemaData(dataDefinition);
          if (saveResult.success && saveResult.id_custom_data_definition && getters.getOverrides && getters.getOverrides.length > 0) {
            const clonedOverrides = JSON.parse(JSON.stringify(getters.getOverrides));
            // define id_custom_data_definition for each override with id_custom_data_definition is equal to 0
            const updatedOverrides = clonedOverrides.map((override: any) => {
              if (override.id_custom_data_definition === 0) {
                return { ...override, id_custom_data_definition: saveResult.id_custom_data_definition };
              }
              return override;
            });
            commit('setOverrides', updatedOverrides);
          }
          result = { success: saveResult.success, error: saveResult.success ? '' : 'Une erreur est survenue lors de la sauvegarde du schéma' };
        }

        if (result.success) {
          const overrideChanges = detectOverrideChanges(getters.getNeededDataExistingOverrides ?? [], getters.getOverrides ?? []);
          console.log('overrideChanges', overrideChanges);
          if (Object.keys(overrideChanges).length > 0) {
            await Promise.all(
              Object.keys(overrideChanges).map(async (key: string) => {
                const targetSystemSchema = key.split('::')[0];
                const targetSystemField = key.split('::')[1];
                const formattedOverrides = overrideChanges[key].map((override: any) => ({
                  id_custom_data_definition: parseInt(override.id_custom_data_definition, 10),
                  custom_field_name: override.custom_field_name,
                  priority: parseInt(override.priority, 10),
                }));
                return saveOverrides(targetSystemSchema, targetSystemField, { overrides: formattedOverrides });
              }),
            );
          }
        }

        return result;
      } catch (error) {
        console.error('Error saving custom object:', error);
        return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
      }
    },

    resetCustomObject: ({ state, commit }: ActionContext<CustomObjectState, State>) => {
      commit('setIdCustomDataDefinition', 0);
      commit('updateSettings', { name: '', description: '' });
      commit('setCurrentEditingElement', null);
      commit('setFields', []);
      commit('setRelationships', []);
      commit('setOverrides', []);
      commit('setNeededData', {
        systemSchemas: [],
        customSchemas: [],
        existingFields: [],
        existingRelationships: [],
        existingOverrides: {},
      });
      commit('incrementRefreshViewComponent');
    },

    /**
     * Handle field actions (add, remove, edit, cancel-edit, save, instant-update-data)
     * @param state - The current state of the custom object
     * @param commit - The commit function to update the state
     * @param action - The action to perform
     * @param fieldData - The field data to update
     * @param id - The id of the field to update
     */
    handleFieldAction: ({ state, commit }: ActionContext<CustomObjectState, State>, { action, fieldData, id }:
         { action: string; fieldData: Field; id: number }) => {
      switch (action) {
        case 'add-new-empty': {
          const newId = getNewElementId(state.fields);
          const clonedFields = [...state.fields];
          clonedFields.push({
            id: newId,
            name: '',
            label: '',
            type: AvailableTypeEnum.TEXT,
            description: '',
            required: false,
            default: null,
            unique_key: false,
            options: [],
          });
          commit('setFields', clonedFields);
          commit('setCurrentEditingElement', { ...JSON.parse(JSON.stringify(state.fields[state.fields.length - 1])), isNew: true, elementType: ElementTypeEnum.FIELD });
          break;
        }
        case 'remove': {
          const fieldToRemove = state.fields.find((f: Field) => f.id === id);
          if (fieldToRemove) {
            const fieldName = fieldToRemove.name;

            // check if the field being removed is currently being edited
            if (state.currentEditingElement && state.currentEditingElement.id === id && state.currentEditingElement.elementType === ElementTypeEnum.FIELD) {
              commit('setCurrentEditingElement', null);
            }

            // remove the target field
            const clonedFields = JSON.parse(JSON.stringify(state.fields));
            const filteredFields = clonedFields.filter((f: Field) => f.id !== id);
            commit('setFields', filteredFields);

            // remove the relationships / overrides using the deleted field as localField
            if (fieldName) {
              const clonedRelationships = JSON.parse(JSON.stringify(state.relationships));
              const filteredRelationships = clonedRelationships.filter((rel: any) => rel.localField !== fieldName);

              // Only commit if relationships were actually removed
              if (filteredRelationships.length !== clonedRelationships.length) {
                commit('setRelationships', filteredRelationships);
              }

              const clonedOverrides = JSON.parse(JSON.stringify(state.overrides));
              const overrideToRemove = clonedOverrides.find((o: any) => o.custom_field_name === fieldName
               && parseInt(o.id_custom_data_definition, 10) === parseInt(state.id_custom_data_definition.toString(), 10));

              if (overrideToRemove) {
                commit('setOverrides', clonedOverrides.filter((o: any) => o.id !== overrideToRemove.id));
              }
            }
          }
          break;
        }
        case 'edit': {
          const fieldToEdit = state.fields.find((f: Field) => f.id === id);
          if (fieldToEdit) {
            commit('setCurrentEditingElement', { ...JSON.parse(JSON.stringify(fieldToEdit)), isNew: false, elementType: ElementTypeEnum.FIELD });
          }
          break;
        }
        case 'cancel-edit': {
          const fieldToCancel = state.fields.find((f: Field) => f.id === id);
          if (fieldToCancel && state.currentEditingElement && state.currentEditingElement.elementType === ElementTypeEnum.FIELD) {
            const clonedFields = JSON.parse(JSON.stringify(state.fields));
            const fieldIndex = clonedFields.findIndex((f: Field) => f.id === id);
            if (fieldIndex !== -1) {
              // Restore original data
              delete state.currentEditingElement.elementType;
              delete state.currentEditingElement.isNew;
              clonedFields[fieldIndex] = { ...state.currentEditingElement };
            }
            commit('setFields', clonedFields);
            commit('setCurrentEditingElement', null);
            commit('incrementRefreshViewComponent');
          }
          break;
        }
        case 'save': {
          const fieldIndex = state.fields.findIndex((f: Field) => f.id === id);
          if (fieldIndex !== -1 && fieldData && state.currentEditingElement && state.currentEditingElement.elementType === ElementTypeEnum.FIELD) {
            const clonedFields = JSON.parse(JSON.stringify(state.fields));
            const oldName = state.currentEditingElement.name;
            const newName = fieldData.name;

            // Update the field
            clonedFields[fieldIndex] = { ...fieldData };
            commit('setFields', clonedFields);

            // Sync the field name in the relationships if name changed
            if (newName !== oldName) {
              const clonedRelationships = JSON.parse(JSON.stringify(state.relationships));
              let relationshipsUpdated = false;

              clonedRelationships.forEach((rel: any) => {
                if (rel.localField === oldName) {
                  // eslint-disable-next-line no-param-reassign
                  rel.localField = newName;
                  relationshipsUpdated = true;

                  // also update originalData if the relation is being edited
                  if (rel.isEditing && rel.originalData) {
                    // eslint-disable-next-line no-param-reassign
                    rel.originalData.localField = newName;
                  }
                }
              });

              // only commit if relationships were actually updated
              if (relationshipsUpdated) {
                commit('setRelationships', clonedRelationships);
              }
            }
            commit('setCurrentEditingElement', null);
          }
          break;
        }
        case 'instant-update-data': {
          const fieldIndex = state.fields.findIndex((f: Field) => f.id === id);
          if (fieldIndex !== -1 && fieldData) {
            const clonedFields = JSON.parse(JSON.stringify(state.fields));
            clonedFields[fieldIndex] = { ...fieldData };
            commit('setFields', clonedFields);
          }
          break;
        }
        default: {
          break;
        }
      }
    },

    /**
     * Handle relation actions (add, remove, edit, cancel-edit, save)
     * @param state - The current state of the custom object
     * @param commit - The commit function to update the state
     * @param action - The action to perform
     * @param relationData - The relation data to update
     * @param id - The id of the relation to update
     */
    handleRelationAction: ({ state, commit }: ActionContext<CustomObjectState, State>, { action, relationData, id }:
         { action: string; relationData: ForeignKeyRow; id: number }) => {
      switch (action) {
        case 'add-new-empty': {
          const newId = getNewElementId(state.relationships);
          const clonedRelationships = [...state.relationships];
          clonedRelationships.push({
            id: newId,
            localField: '',
            schema_type: 'system',
            schema: '',
            field: '',
            selectedTargetSchema: null,
          });
          commit('setRelationships', clonedRelationships);
          commit('setCurrentEditingElement',
            { ...JSON.parse(JSON.stringify(clonedRelationships[clonedRelationships.length - 1])), isNew: true, elementType: ElementTypeEnum.RELATION });
          break;
        }
        case 'remove': {
          const relationToRemove = state.relationships.find((rel: ForeignKeyRow) => rel.id === id);
          if (relationToRemove) {
            // check if the relation being removed is currently being edited
            if (state.currentEditingElement && state.currentEditingElement.id === id && state.currentEditingElement.elementType === ElementTypeEnum.RELATION) {
              commit('setCurrentEditingElement', null);
            }

            // remove the target relation
            const clonedRelationships = JSON.parse(JSON.stringify(state.relationships));
            const filteredRelationships = clonedRelationships.filter((rel: ForeignKeyRow) => rel.id !== id);
            commit('setRelationships', filteredRelationships);
          }
          break;
        }
        case 'edit': {
          const relationToEdit = state.relationships.find((rel: ForeignKeyRow) => rel.id === id);
          if (relationToEdit) {
            commit('setCurrentEditingElement', { ...JSON.parse(JSON.stringify(relationToEdit)), isNew: false, elementType: ElementTypeEnum.RELATION });
          }
          break;
        }
        case 'cancel-edit': {
          const relationToCancel = state.relationships.find((rel: ForeignKeyRow) => rel.id === id);
          if (relationToCancel && state.currentEditingElement && state.currentEditingElement.elementType === ElementTypeEnum.RELATION) {
            const clonedRelationships = JSON.parse(JSON.stringify(state.relationships));
            const relationIndex = clonedRelationships.findIndex((rel: ForeignKeyRow) => rel.id === id);
            if (relationIndex !== -1) {
              clonedRelationships[relationIndex] = { ...state.currentEditingElement };
            }
            commit('setRelationships', clonedRelationships);
            commit('setCurrentEditingElement', null);
          }
          break;
        }
        case 'save': {
          const relationIndex = state.relationships.findIndex((rel: ForeignKeyRow) => rel.id === id);
          if (relationIndex !== -1 && state.currentEditingElement && state.currentEditingElement.elementType === ElementTypeEnum.RELATION) {
            commit('setCurrentEditingElement', null);
          }
          break;
        }
        default: {
          break;
        }
      }
    },

    handleOverrideAction: ({ state, commit }: ActionContext<CustomObjectState, State>, {
      action, id, overrideItem, overridesPriority,
    }:
         { action: string; id: number; overrideItem: any; overridesPriority: any }) => {
      switch (action) {
        case 'add-new-empty': {
          const newId = getNewElementId(state.overrides);
          commit('setCurrentEditingElement', {
            id: newId,
            elementType: ElementTypeEnum.OVERRIDE,
          });
          break;
        }
        case 'save': {
          if (state.currentEditingElement && state.currentEditingElement.elementType === ElementTypeEnum.OVERRIDE) {
            const clonedOverrides = JSON.parse(JSON.stringify(state.overrides));

            // get the max priority for the combo system_field_name/target_system_schema
            const sameFieldOverrides = clonedOverrides.filter((override: any) => override.system_field_name === overrideItem.system_field_name
              && override.target_system_schema === overrideItem.target_system_schema);

            let priorityMax = 0;
            if (sameFieldOverrides.length > 0) {
              priorityMax = Math.max(...sameFieldOverrides.map((override: any) => parseInt(override.priority, 10)));
            }

            clonedOverrides.push({ ...overrideItem, priority: priorityMax + 1 });

            commit('setOverrides', clonedOverrides);
            commit('setCurrentEditingElement', null);
          }
          break;
        }
        case 'remove': {
          const overrideToRemove = id ? state.overrides.find((o: any) => o.id === id) : null;

          // if overrideToRemove exists, remove it from the overrides
          if (overrideToRemove) {
            const clonedOverrides = JSON.parse(JSON.stringify(state.overrides));
            const filteredOverrides = clonedOverrides.filter((o: any) => o.id !== id);

            // get the origin target_system_schema/system_field_name of the override to remove
            const { target_system_schema, system_field_name } = overrideToRemove;

            // sort the priorities of the same combo
            const sameComboOverrides = filteredOverrides.filter((override: any) => override.target_system_schema === target_system_schema
              && override.system_field_name === system_field_name);

            // sort by current priority and reassign the priorities
            sameComboOverrides.sort((a: any, b: any) => parseInt(a.priority, 10) - parseInt(b.priority, 10));

            // reassign the priorities (1, 2, 3, etc.) for all overrides
            const updatedOverrides = filteredOverrides.map((override: any) => {
              if (
                override.target_system_schema === target_system_schema
                && override.system_field_name === system_field_name
              ) {
                // Trouver le nouvel index dans le tableau trié
                const index = sameComboOverrides.findIndex(
                  (o: any) => o.id === override.id,
                );
                return {
                  ...override,
                  priority: index !== -1 ? index + 1 : override.priority,
                };
              }
              return override;
            });

            // Remove the override to delete
            const finalOverrides = updatedOverrides.filter((o: any) => o.id !== id);
            commit('setOverrides', finalOverrides);
          }
          // if overrideToRemove does not exist yet (new adding override), only remove the current editing element
          commit('setCurrentEditingElement', null);
          break;
        }
        case 'save-priorities': {
          const clonedOverrides = JSON.parse(JSON.stringify(state.overrides));
          overridesPriority.forEach((o: any) => {
            const index = clonedOverrides.findIndex((c: any) => c.id === o.id);
            if (index !== -1) {
              clonedOverrides[index] = { ...o, priority: o.priority };
            }
          });

          commit('setOverrides', clonedOverrides);
          break;
        }
        case 'update': {
          const clonedOverrides = JSON.parse(JSON.stringify(state.overrides));
          const overrideIndex = clonedOverrides.findIndex((o: any) => o.id === id);
          if (overrideIndex !== -1) {
            clonedOverrides[overrideIndex] = { ...overrideItem };
          }
          commit('setOverrides', clonedOverrides);
          break;
        }
        default: {
          break;
        }
      }
    },
  },
};
